import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  OverlayTrigger,
  ProgressBar,
  Row,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import { Navigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faDownload,
  faFile,
  faFileAudio,
  faFileImage,
  faFileVideo,
  faFileZipper,
  faFolder,
  faPlus,
  faSync,
  faTrash as faTrashIcon,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { Download, Edit, Info, Trash } from "react-feather";
import { confirmAlert } from "react-confirm-alert";
import { ShowConfirm } from "../../components/Alerts.js";
import { ColumnSortingTable } from "../../components/Table.js";
import { domainConfig } from "../../config.js";
import NotyfContext from "../../contexts/NotyfContext.js";
import useAuth from "../../hooks/useAuth.js";
import useLayout from "../../hooks/useLayout.js";

const STORAGE_SCOPES = [
  {
    key: "media",
    label: "Mídia",
    serverLabel: "servidor de mídia",
    description: "Pasta destinada para armazenamento de imagens, áudios e vídeos locais",
    domain: "https://images.r1eventech.com.br/r1",
  },
  {
    key: "projects",
    label: "Projetos",
    serverLabel: "servidor de projetos",
    description: "Pasta destinada para hospedagem versátil de projetos",
    domain: "https://projetos.r1eventech.com.br/",
  },
  {
    key: "cadastros",
    label: "Cadastros",
    serverLabel: "servidor de cadastros",
    description: "Pasta destinada para servidor websites de cadastro",
    domain: "https://cadastros.r1eventech.com.br/",
  },
];

const DEFAULT_LIMITS = {
  maxFiles: 10000,
  maxFileBytes: 500 * 1024 * 1024,
  maxTotalBytes: 2 * 1024 * 1024 * 1024,
};

const UPLOAD_CHUNK_MAX_FILES = 500;
const UPLOAD_CHUNK_MAX_BYTES = 250 * 1024 * 1024;

const STORAGE_TIME_ZONE = "America/Sao_Paulo";

const STORAGE_ERROR_MESSAGES = Object.freeze({
  INVALID_STORAGE_SCOPE: "O escopo de armazenamento informado é inválido.",
  INVALID_STORAGE_PATH: "O caminho informado é inválido.",
  STORAGE_PATH_ESCAPE: "O caminho informado não é permitido.",
  REMOTE_SYMLINK_NOT_ALLOWED: "Links simbólicos não podem ser gerenciados aqui.",
  REMOTE_PATH_EXISTS: "Já existe um arquivo ou pasta com esse nome. Atualize a pasta, escolha outro nome ou remova o item existente.",
  REMOTE_PATH_NOT_FOUND: "O arquivo ou pasta não foi encontrado no servidor remoto. Atualize a pasta e tente novamente.",
  REMOTE_NOT_A_FILE: "O item selecionado não é um arquivo.",
  REMOTE_NOT_A_DIRECTORY: "O item selecionado não é uma pasta.",
  INVALID_UPLOAD_MANIFEST: "Não foi possível interpretar a estrutura do envio.",
  DUPLICATE_UPLOAD_PATH: "O envio contém caminhos de arquivo duplicados. Selecione os arquivos novamente sem repetir nomes.",
  NO_FILES: "Nenhum arquivo foi selecionado.",
  UPLOAD_TOTAL_TOO_LARGE: "O lote excede o limite total permitido.",
  LIMIT_FILE_SIZE: "Um dos arquivos excede o tamanho máximo permitido.",
  LIMIT_FILE_COUNT: "O envio excede o número máximo de arquivos.",
  LIMIT_FIELD_SIZE: "Os dados do envio excedem o limite permitido.",
  LIMIT_UNEXPECTED_FILE: "O envio contém um campo de arquivo inesperado.",
  UPLOAD_SESSIONS_BUSY: "Existem muitos envios grandes em andamento. Tente novamente em alguns minutos.",
  INVALID_UPLOAD_SESSION: "Os dados da sessão de envio são inválidos. Inicie o envio novamente.",
  UPLOAD_SESSION_NOT_FOUND: "A sessão de envio expirou. Selecione os arquivos novamente para iniciar um novo envio.",
  UPLOAD_CHUNK_OUT_OF_ORDER: "Uma parte do envio chegou fora de ordem. Selecione os arquivos novamente e tente iniciar o envio de novo.",
  UPLOAD_BATCH_MISMATCH: "O envio não corresponde à quantidade ou ao tamanho declarado. Selecione os arquivos novamente.",
  STORAGE_UPLOAD_INVALID: "O envio não pôde ser validado.",
  INVALID_MOVE: "Informe a origem e o destino para renomear o item.",
  REMOTE_ARCHIVE_TOO_MANY_FILES: "A pasta contém arquivos demais para gerar um ZIP.",
  REMOTE_ARCHIVE_TOO_LARGE: "A pasta é muito grande para gerar um ZIP.",
  REMOTE_ARCHIVE_TOO_DEEP: "A pasta tem níveis demais para gerar um ZIP.",
  REMOTE_ARCHIVE_SOURCE_NOT_FOUND: "Um arquivo mudou ou foi removido durante a geração do ZIP.",
  INVALID_STORAGE_SELECTION: "A seleção de itens é inválida.",
  STORAGE_SELECTION_TOO_LARGE: "A seleção contém itens demais.",
  REMOTE_ARCHIVE_NAME_CONFLICT: "Não foi possível gerar um ZIP sem nomes duplicados.",
  REMOTE_MOVE_INTO_DESCENDANT: "Uma pasta não pode ser movida para dentro dela mesma.",
  REMOTE_ROOT_ENTRY_RENAME_NOT_ALLOWED: "As pastas principais não podem ser renomeadas por aqui.",
  REMOTE_BATCH_PARTIAL: "A operação foi concluída parcialmente. Atualize a pasta antes de tentar novamente.",
  SFTP_NOT_CONFIGURED: "O armazenamento remoto está indisponível porque não foi configurado no servidor. Avise o administrador.",
  SFTP_HOST_KEY_NOT_VERIFIED: "A identidade do servidor remoto não pôde ser verificada.",
  REMOTE_STORAGE_ERROR: "O servidor de armazenamento não conseguiu concluir a operação. Atualize a pasta e tente novamente.",
  MEDIA_UPLOAD_NOT_CONFIGURED: "O destino dos arquivos de mídia não está configurado.",
  MEDIA_UPLOAD_INVALID: "O envio de mídia é inválido.",
  MEDIA_CONTENT_INVALID: "O conteúdo do arquivo não corresponde ao formato informado.",
  MEDIA_LIST_FAILED: "Não foi possível carregar os arquivos de mídia.",
  MEDIA_INVALID_PATH: "O nome do arquivo de mídia é inválido.",
  MEDIA_PATH_NOT_FOUND: "O arquivo de mídia não foi encontrado no servidor.",
  MEDIA_PATH_EXISTS: "Já existe um arquivo de mídia com esse nome.",
  MEDIA_NOT_A_FILE: "O item selecionado não é um arquivo de mídia.",
  MEDIA_STORAGE_ERROR: "Não foi possível concluir a operação nos arquivos de mídia.",
});

const STORAGE_STATUS_MESSAGES = Object.freeze({
  400: "A solicitação para o armazenamento não é válida. Atualize a pasta e tente novamente.",
  401: "Sua sessão expirou. Entre novamente para continuar.",
  403: "Você não tem permissão para realizar esta operação.",
  404: "O item não foi encontrado. Atualize a pasta para ver o estado atual.",
  408: "O armazenamento demorou demais para responder. Tente novamente.",
  409: "A operação entrou em conflito porque a pasta foi alterada. Atualize a pasta e tente novamente.",
  413: "O envio excede o limite permitido para o armazenamento.",
  429: "Há muitas operações de armazenamento em andamento. Aguarde e tente novamente.",
  500: "O servidor encontrou um problema ao processar o armazenamento. Tente novamente.",
  502: "Não foi possível conectar ao servidor de armazenamento. Tente novamente em instantes.",
  503: "O armazenamento remoto está indisponível no momento. Tente novamente mais tarde.",
  504: "O armazenamento demorou demais para concluir a operação. Tente novamente.",
});

const getStorageErrorMessage = async (error, fallback) => {
  let responseData = error?.response?.data;
  if (typeof Blob !== "undefined" && responseData instanceof Blob) {
    try {
      responseData = JSON.parse(await responseData.text());
    } catch (_) {
      responseData = null;
    }
  }

  if (typeof responseData === "string") {
    try {
      responseData = JSON.parse(responseData);
    } catch (_) {
      responseData = null;
    }
  }

  const code = String(responseData?.code || "").toUpperCase();
  const status = Number(error?.response?.status);
  const statusMessage = Number.isInteger(status) ? STORAGE_STATUS_MESSAGES[status] : "";
  const codeMessage = STORAGE_ERROR_MESSAGES[code];

  if (codeMessage && code !== "REMOTE_STORAGE_ERROR") return codeMessage;
  if (statusMessage) return statusMessage;
  return codeMessage || fallback;
};

const MEDIA_UPLOAD_MAX_FILES = 8;
const MEDIA_UPLOAD_MAX_FILE_BYTES = 500 * 1024 * 1024;
const MEDIA_UPLOAD_ACCEPT = "image/*,audio/*,video/*";
const MEDIA_UPLOAD_EXTENSIONS = new Set([
  "jpg", "jpeg", "png", "gif", "webp", "heic", "heif",
  "mp4", "webm", "mov", "m4v", "mp3", "wav", "ogg", "m4a", "aac", "flac",
]);

const MEDIA_PREVIEW_EXTENSIONS = {
  image: new Set(["jpg", "jpeg", "png", "gif", "webp", "svg"]),
  video: new Set(["mp4", "webm", "mov", "m4v"]),
  audio: new Set(["mp3", "wav", "ogg", "m4a", "aac", "flac"]),
};

const getMediaExtension = (name) => String(name || "").split(".").pop()?.toLowerCase() || "";

const getMediaKind = (name) => {
  const extension = getMediaExtension(name);
  return Object.entries(MEDIA_PREVIEW_EXTENSIONS).find(([, extensions]) => extensions.has(extension))?.[0] || "file";
};

const getMediaAssetUrl = (name) => {
  const entry = name && typeof name === "object" ? name : null;
  if (entry?.public_url) return entry.public_url;

  const mediaName = entry?.name || name;
  const imageServer = String(domainConfig.imageServer || "").replace(/\/$/, "");
  return imageServer && mediaName ? `${imageServer}/r1/${encodeURIComponent(mediaName)}` : "";
};

const wrapMediaPreviewWithLink = (content, url, name) => (url ? (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="d-inline-flex align-items-center"
    title={`Abrir ${name}`}
  >
    {content}
  </a>
) : content);

const MediaPreview = ({ entry }) => {
  const [previewFailed, setPreviewFailed] = useState(false);
  const mediaKind = getMediaKind(entry?.name);
  const previewUrl = getMediaAssetUrl(entry);

  if (previewFailed || !previewUrl || mediaKind === "audio" || mediaKind === "file") {
    const icon = mediaKind === "image" ? faFileImage : mediaKind === "video" ? faFileVideo : mediaKind === "audio" ? faFileAudio : faFile;
    return wrapMediaPreviewWithLink((
      <span className="d-inline-flex align-items-center justify-content-center text-secondary" style={{ width: "64px", height: "48px" }} title={`Tipo: ${mediaKind}`}>
        <FontAwesomeIcon icon={icon} size="lg" />
      </span>
    ), previewUrl, entry.name);
  }

  const previewStyle = {
    width: "64px",
    height: "48px",
    objectFit: "cover",
    borderRadius: "4px",
    backgroundColor: "#eef1f4",
  };

  if (mediaKind === "video") {
    return wrapMediaPreviewWithLink((
      <video
        src={previewUrl}
        muted
        preload="metadata"
        aria-label={`Prévia de ${entry.name}`}
        style={previewStyle}
        onError={() => setPreviewFailed(true)}
      />
    ), previewUrl, entry.name);
  }

  return wrapMediaPreviewWithLink((
    <img
      src={previewUrl}
      alt={`Prévia de ${entry.name}`}
      style={previewStyle}
      onError={() => setPreviewFailed(true)}
    />
  ), previewUrl, entry.name);
};

const isHiddenStorageEntry = (entry) => entry?.name === ".htaccess";

const formatBytes = (value) => {
  const bytes = Number(value || 0);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatModifiedDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return `${date.toLocaleDateString("pt-BR", { timeZone: STORAGE_TIME_ZONE })} ${date.toLocaleTimeString("pt-BR", {
    timeZone: STORAGE_TIME_ZONE,
    hour12: false,
  })}`;
};

const normalizePath = (value) => String(value || "").replaceAll("\\", "/").replace(/^\/+|\/+$/g, "");

const joinPath = (...parts) => parts.map(normalizePath).filter(Boolean).join("/");

const getParentPath = (value) => {
  const normalized = normalizePath(value);
  const separatorIndex = normalized.lastIndexOf("/");
  return separatorIndex === -1 ? "" : normalized.slice(0, separatorIndex);
};

const getEntryPath = (currentPath, entryName) => joinPath(currentPath, entryName);

const getParentFolderName = (currentPath, scope) => {
  const parts = normalizePath(currentPath).split("/").filter(Boolean);
  return parts[parts.length - 1] || (scope === "media" ? "midia" : scope === "projects" ? "projetos" : "cadastros");
};

const getArchiveFileName = (currentPath, scope) => `${getParentFolderName(currentPath, scope)}.zip`;

const isMediaFile = (file) => {
  const extension = String(file?.name || "").split(".").pop()?.toLowerCase() || "";
  const mimeType = String(file?.type || "").toLowerCase();
  return ["image/", "audio/", "video/"].some((prefix) => mimeType.startsWith(prefix))
    || MEDIA_UPLOAD_EXTENSIONS.has(extension);
};

const getSelectedRelativePath = (file) => normalizePath(file.webkitRelativePath || file.name);

const getFileSize = (file) => Math.max(0, Number(file?.size || 0));

const isRetryableUploadError = (error) => {
  const status = error?.response?.status;
  if (status === undefined || status === null) return true;

  const numericStatus = Number(status);
  return !Number.isFinite(numericStatus)
    || numericStatus === 408
    || numericStatus === 429
    || numericStatus >= 500;
};

const splitUploadFiles = (files, maxFiles = UPLOAD_CHUNK_MAX_FILES, maxBytes = UPLOAD_CHUNK_MAX_BYTES) => {
  const chunks = [];
  let currentChunk = [];
  let currentBytes = 0;

  files.forEach((file) => {
    const fileBytes = getFileSize(file);
    const wouldExceedChunk = currentChunk.length > 0
      && (currentChunk.length >= maxFiles || currentBytes + fileBytes > maxBytes);

    if (wouldExceedChunk) {
      chunks.push(currentChunk);
      currentChunk = [];
      currentBytes = 0;
    }

    currentChunk.push(file);
    currentBytes += fileBytes;

    if (fileBytes > maxBytes) {
      chunks.push(currentChunk);
      currentChunk = [];
      currentBytes = 0;
    }
  });

  if (currentChunk.length) chunks.push(currentChunk);
  return chunks;
};

const formatCount = (value) => Number(value || 0).toLocaleString("pt-BR");

const getFolderAccessUrl = (scope, relativePath) => {
  const host = scope === "projects"
    ? "https://projetos.r1eventech.com.br"
    : "https://cadastros.r1eventech.com.br";
  const encodedPath = normalizePath(relativePath)
    .split("/")
    .filter(Boolean)
    .map((part) => encodeURIComponent(part))
    .join("/");
  return `${host}/${encodedPath}`;
};

const StorageManager = () => {
  const { tipo } = useAuth();
  const { setLoading: setGlobalLoading } = useLayout();
  const notyf = useContext(NotyfContext);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const loadRequestIdRef = useRef(0);
  const [scope, setScope] = useState("media");
  const [currentPath, setCurrentPath] = useState("");
  const [entries, setEntries] = useState([]);
  const [tableInstanceVersion, setTableInstanceVersion] = useState(0);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyAction, setBusyAction] = useState("");
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [renameEntry, setRenameEntry] = useState(null);
  const [renameName, setRenameName] = useState("");
  const [propertyEntry, setPropertyEntry] = useState(null);
  const [limits, setLimits] = useState(DEFAULT_LIMITS);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [selectedPaths, setSelectedPaths] = useState(() => new Set());
  const [selectionBarMetrics, setSelectionBarMetrics] = useState({ left: 240, height: 66 });

  const selectedScope = useMemo(
    () => STORAGE_SCOPES.find((item) => item.key === scope) || STORAGE_SCOPES[0],
    [scope],
  );
  const isMediaScope = scope === "media";

  const selectableEntries = useMemo(
    () => entries.filter((entry) => entry.type !== "symlink"),
    [entries],
  );

  const selectedEntries = useMemo(
    () => entries.filter((entry) => selectedPaths.has(getEntryPath(currentPath, entry.name))),
    [currentPath, entries, selectedPaths],
  );

  const allEntriesSelected = selectableEntries.length > 0
    && selectableEntries.every((entry) => selectedPaths.has(getEntryPath(currentPath, entry.name)));

  useEffect(() => {
    const updateSelectionBarMetrics = () => {
      const sidebar = document.querySelector(".sidebar");
      const navbar = document.querySelector(".navbar-bg.navbar");
      const sidebarRect = sidebar?.getBoundingClientRect();
      const navbarRect = navbar?.getBoundingClientRect();

      setSelectionBarMetrics({
        left: Math.max(0, Math.round(sidebarRect?.right ?? 240)),
        height: Math.max(1, Math.round(navbarRect?.height || 66)),
      });
    };

    updateSelectionBarMetrics();
    window.addEventListener("resize", updateSelectionBarMetrics);
    const sidebar = document.querySelector(".sidebar");
    const observer = typeof MutationObserver !== "undefined" && sidebar
      ? new MutationObserver(updateSelectionBarMetrics)
      : null;
    observer?.observe(sidebar, { attributes: true, subtree: true });

    return () => {
      window.removeEventListener("resize", updateSelectionBarMetrics);
      observer?.disconnect();
    };
  }, []);

  const showNotification = useCallback((type, message) => {
    notyf.open({ type, message, ripple: true, dismissible: true });
  }, [notyf]);

  const loadTree = useCallback(async ({ showLoading = true } = {}) => {
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;

    if (showLoading) {
      setLoading(true);
      setGlobalLoading(true);
    }
    setError("");
    setSelectedPaths(new Set());

    try {
      const response = isMediaScope
        ? await axios.get("/api/upload/media/r1")
        : await axios.get(`/api/storage/${scope}/tree`, {
          params: { path: currentPath },
        });
      const remoteEntries = Array.isArray(response.data?.entries) ? response.data.entries : [];
      if (requestId !== loadRequestIdRef.current) return;
      setEntries(remoteEntries.filter((entry) => !isHiddenStorageEntry(entry)));
      setTableInstanceVersion((current) => current + 1);
      setHasLoadedOnce(true);
      if (response.data?.limits) {
        setLimits({
          maxFiles: Number(response.data.limits.maxFiles) || DEFAULT_LIMITS.maxFiles,
          maxFileBytes: Number(response.data.limits.maxFileBytes) || DEFAULT_LIMITS.maxFileBytes,
          maxTotalBytes: Number(response.data.limits.maxTotalBytes) || DEFAULT_LIMITS.maxTotalBytes,
        });
      } else if (isMediaScope) {
        setLimits({
          maxFiles: DEFAULT_LIMITS.maxFiles,
          maxFileBytes: MEDIA_UPLOAD_MAX_FILE_BYTES,
          maxTotalBytes: DEFAULT_LIMITS.maxTotalBytes,
        });
      }
    } catch (requestError) {
      if (requestId !== loadRequestIdRef.current) return;
      setError(await getStorageErrorMessage(
        requestError,
        isMediaScope ? "Não foi possível carregar os arquivos de mídia." : "Não foi possível carregar a pasta remota.",
      ));
    } finally {
      if (showLoading && requestId === loadRequestIdRef.current) {
        setLoading(false);
        setGlobalLoading(false);
      }
    }
  }, [currentPath, isMediaScope, scope, setGlobalLoading]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const handleScopeChange = (nextScope) => {
    setScope(nextScope);
    setCurrentPath("");
    setPropertyEntry(null);
    setSelectedPaths(new Set());
  };

  const handleNavigate = (nextPath) => {
    setCurrentPath(normalizePath(nextPath));
    setPropertyEntry(null);
    setSelectedPaths(new Set());
  };

  const handleSelectEntry = (entry, checked) => {
    const relativePath = getEntryPath(currentPath, entry.name);
    setSelectedPaths((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(relativePath);
      } else {
        next.delete(relativePath);
      }
      return next;
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedPaths(new Set(selectableEntries.map((entry) => getEntryPath(currentPath, entry.name))));
    } else {
      setSelectedPaths(new Set());
    }
  };

  const handleDownload = async (entry) => {
    const relativePath = getEntryPath(currentPath, entry.name);
    setBusyAction(`download:${relativePath}`);
    setGlobalLoading(true);

    try {
      const response = await axios.get(isMediaScope ? "/api/upload/media/r1/download" : `/api/storage/${scope}/download`, {
        params: { path: relativePath },
        responseType: "blob",
      });
      const objectUrl = window.URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = entry.name;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (requestError) {
      showNotification("danger", await getStorageErrorMessage(requestError, "Não foi possível baixar o arquivo."));
    } finally {
      setGlobalLoading(false);
      setBusyAction("");
    }
  };

  const handleDownloadArchive = async (entry) => {
    const relativePath = getEntryPath(currentPath, entry.name);
    setBusyAction(`archive:${relativePath}`);
    setGlobalLoading(true);

    try {
      const response = await axios.get(isMediaScope ? "/api/upload/media/r1/archive" : `/api/storage/${scope}/archive`, {
        params: { path: relativePath },
        responseType: "blob",
      });
      const objectUrl = window.URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = getArchiveFileName(currentPath, scope);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (requestError) {
      showNotification("danger", await getStorageErrorMessage(requestError, "Não foi possível baixar a pasta como ZIP."));
    } finally {
      setGlobalLoading(false);
      setBusyAction("");
    }
  };

  const handleDownloadSelection = async () => {
    const paths = selectedEntries.map((entry) => getEntryPath(currentPath, entry.name));
    if (!paths.length) return;

    setBusyAction("bulk-download");
    setGlobalLoading(true);

    try {
      const response = await axios.post(isMediaScope ? "/api/upload/media/r1/archive" : `/api/storage/${scope}/archive`, { paths }, {
        responseType: "blob",
      });
      const objectUrl = window.URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = getArchiveFileName(currentPath, scope);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (requestError) {
      showNotification("danger", await getStorageErrorMessage(requestError, "Não foi possível baixar os itens selecionados."));
    } finally {
      setGlobalLoading(false);
      setBusyAction("");
    }
  };

  const handleDownloadSelectionIndividually = async () => {
    const items = [...selectedEntries];
    if (!items.length) return;

    setBusyAction("bulk-download-individually");
    setGlobalLoading(true);

    try {
      for (const entry of items) {
        const relativePath = getEntryPath(currentPath, entry.name);
        const isDirectory = entry.type === "directory";
        const response = await axios.get(
          isMediaScope
            ? "/api/upload/media/r1/download"
            : isDirectory ? `/api/storage/${scope}/archive` : `/api/storage/${scope}/download`,
          {
            params: { path: relativePath },
            responseType: "blob",
          },
        );
        const objectUrl = window.URL.createObjectURL(response.data);
        const anchor = document.createElement("a");
        anchor.href = objectUrl;
        anchor.download = isDirectory ? getArchiveFileName(relativePath, scope) : entry.name;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(objectUrl);
      }
    } catch (requestError) {
      showNotification("danger", await getStorageErrorMessage(requestError, "Não foi possível baixar os itens individualmente."));
    } finally {
      setGlobalLoading(false);
      setBusyAction("");
    }
  };

  const handleMediaUpload = async (files) => {
    if (files.length > MEDIA_UPLOAD_MAX_FILES) {
      showNotification("danger", `Selecione no máximo ${MEDIA_UPLOAD_MAX_FILES} arquivos de mídia por envio.`);
      return;
    }

    const invalidFile = files.find((file) => (
      Number(file.size || 0) > MEDIA_UPLOAD_MAX_FILE_BYTES || !isMediaFile(file)
    ));
    if (invalidFile) {
      showNotification(
        "danger",
        Number(invalidFile.size || 0) > MEDIA_UPLOAD_MAX_FILE_BYTES
          ? `${invalidFile.name} excede o limite de ${formatBytes(MEDIA_UPLOAD_MAX_FILE_BYTES)}.`
          : `${invalidFile.name} não é um arquivo de mídia permitido.`,
      );
      return;
    }

    setBusyAction("media-upload");
    setGlobalLoading(true);
    let uploadedCount = 0;

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file, file.name);
        try {
          await axios.post("/api/upload/media/r1", formData);
          uploadedCount += 1;
        } catch (requestError) {
          showNotification("danger", await getStorageErrorMessage(requestError, `Não foi possível enviar ${file.name}.`));
        }
      }

      if (uploadedCount > 0) {
        showNotification("success", uploadedCount === 1 ? "Arquivo enviado." : `${uploadedCount} arquivos enviados.`);
      }
      if (uploadedCount > 0) await loadTree({ showLoading: false });
    } finally {
      setGlobalLoading(false);
      setBusyAction("");
    }
  };

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    if (isMediaScope) {
      await handleMediaUpload(files);
      return;
    }
    if (!currentPath) {
      showNotification("danger", "Entre em uma pasta antes de enviar arquivos.");
      return;
    }

    if (files.length > limits.maxFiles) {
      showNotification("danger", `Selecione no máximo ${limits.maxFiles} arquivos por lote.`);
      return;
    }

    const totalBytes = files.reduce((total, file) => total + getFileSize(file), 0);
    const oversizedFile = files.find((file) => getFileSize(file) > limits.maxFileBytes);
    if (oversizedFile) {
      showNotification("danger", `${oversizedFile.name} excede o limite de ${formatBytes(limits.maxFileBytes)}.`);
      return;
    }
    if (totalBytes > limits.maxTotalBytes) {
      showNotification("danger", `O lote excede o limite de ${formatBytes(limits.maxTotalBytes)}.`);
      return;
    }

    const manifest = files.map((file) => ({
      relativePath: joinPath(currentPath, getSelectedRelativePath(file)),
    }));
    const uniquePaths = new Set(manifest.map((item) => item.relativePath.toLowerCase()));
    if (uniquePaths.size !== manifest.length) {
      showNotification("danger", "Existem arquivos duplicados no lote selecionado.");
      return;
    }

    const chunks = splitUploadFiles(files);
    setBusyAction("upload");
    setGlobalLoading(true);
    setUploadProgress({
      completedFiles: 0,
      totalFiles: files.length,
      completedBytes: 0,
      totalBytes,
      chunkIndex: 0,
      chunkCount: chunks.length,
      chunkLoadedBytes: 0,
      chunkBytes: getFileSize(chunks[0]?.[0]),
    });

    let uploadId = "";
    let completedFiles = 0;
    let completedBytes = 0;

    try {
      const sessionResponse = await axios.post(`/api/storage/${scope}/upload-sessions`, {
        file_count: files.length,
        total_bytes: totalBytes,
        chunk_count: chunks.length,
      });
      uploadId = String(sessionResponse.data?.upload_id || "");
      if (!uploadId) throw new Error("Upload session was not created");

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex += 1) {
        const chunkFiles = chunks[chunkIndex];
        const chunkBytes = chunkFiles.reduce((total, file) => total + getFileSize(file), 0);
        let chunkUploaded = false;
        let chunkResponse = null;

        for (let attempt = 0; attempt < 3 && !chunkUploaded; attempt += 1) {
          const formData = new FormData();
          chunkFiles.forEach((file) => formData.append("files", file, file.name));
          formData.append("manifest", JSON.stringify(chunkFiles.map((file) => ({
            relativePath: joinPath(currentPath, getSelectedRelativePath(file)),
          }))));

          try {
            chunkResponse = await axios.post(`/api/storage/${scope}/files`, formData, {
              headers: {
                "X-Storage-Upload-Id": uploadId,
                "X-Storage-Upload-Chunk-Index": String(chunkIndex),
                "X-Storage-Upload-Chunk-Count": String(chunks.length),
              },
              onUploadProgress: (progressEvent) => {
                const loaded = Math.min(chunkBytes, Math.max(0, Number(progressEvent.loaded || 0)));
                setUploadProgress({
                  completedFiles,
                  totalFiles: files.length,
                  completedBytes,
                  totalBytes,
                  chunkIndex,
                  chunkCount: chunks.length,
                  chunkLoadedBytes: loaded,
                  chunkBytes,
                });
              },
            });
            chunkUploaded = true;
          } catch (requestError) {
            if (attempt === 2 || !isRetryableUploadError(requestError)) throw requestError;
            await new Promise((resolve) => window.setTimeout(resolve, 750 * (attempt + 1)));
          }
        }

        if (chunkIndex === chunks.length - 1 && chunkResponse?.data?.complete !== true) {
          throw new Error("The upload session did not complete");
        }

        completedFiles += chunkFiles.length;
        completedBytes += chunkBytes;
        setUploadProgress({
          completedFiles,
          totalFiles: files.length,
          completedBytes,
          totalBytes,
          chunkIndex: chunkIndex + 1,
          chunkCount: chunks.length,
          chunkLoadedBytes: 0,
          chunkBytes: getFileSize(chunks[chunkIndex + 1]?.[0]),
        });
      }

      showNotification("success", files.length === 1 ? "Arquivo enviado." : `${files.length} arquivos enviados.`);
      await loadTree({ showLoading: false });
    } catch (requestError) {
      showNotification("danger", await getStorageErrorMessage(requestError, "Não foi possível enviar os arquivos."));
      await loadTree({ showLoading: false });
    } finally {
      setGlobalLoading(false);
      setBusyAction("");
      setUploadProgress(null);
    }
  };

  const handleCreateFolder = async (event) => {
    event.preventDefault();
    const name = folderName.trim();
    if (!name || name === "." || name === ".." || /[\\/\u0000-\u001f\u007f]/.test(name)) {
      showNotification("danger", "Informe um nome de pasta válido.");
      return;
    }

    setBusyAction("folder");
    setGlobalLoading(true);
    try {
      await axios.post(`/api/storage/${scope}/folders`, { path: joinPath(currentPath, name) });
      setFolderModalOpen(false);
      setFolderName("");
      showNotification("success", "Pasta criada.");
      await loadTree({ showLoading: false });
    } catch (requestError) {
      showNotification("danger", await getStorageErrorMessage(requestError, "Não foi possível criar a pasta."));
    } finally {
      setGlobalLoading(false);
      setBusyAction("");
    }
  };

  const handleRename = async (event) => {
    event.preventDefault();
    const name = renameName.trim();
    if (!renameEntry || !name || name === "." || name === ".." || /[\\/\u0000-\u001f\u007f]/.test(name)) {
      showNotification("danger", "Informe um nome válido.");
      return;
    }

    const fromPath = getEntryPath(currentPath, renameEntry.name);
    const toPath = joinPath(getParentPath(fromPath), name);
    setBusyAction(`rename:${fromPath}`);
    setGlobalLoading(true);

    try {
      await axios.patch(isMediaScope ? "/api/upload/media/r1/move" : `/api/storage/${scope}/move`, { from: fromPath, to: toPath });
      setRenameEntry(null);
      setRenameName("");
      showNotification("success", "Item renomeado.");
      await loadTree({ showLoading: false });
    } catch (requestError) {
      showNotification("danger", await getStorageErrorMessage(requestError, "Não foi possível renomear o item."));
    } finally {
      setGlobalLoading(false);
      setBusyAction("");
    }
  };

  const handleDelete = async (entry) => {
    const relativePath = getEntryPath(currentPath, entry.name);
    setBusyAction(`delete:${relativePath}`);
    setGlobalLoading(true);

    try {
      await axios.delete(isMediaScope ? "/api/upload/media/r1" : `/api/storage/${scope}`, { params: { path: relativePath } });
      showNotification("success", "Item removido.");
      await loadTree({ showLoading: false });
    } catch (requestError) {
      showNotification("danger", await getStorageErrorMessage(requestError, "Não foi possível remover o item."));
    } finally {
      setGlobalLoading(false);
      setBusyAction("");
    }
  };

  const handleDeleteSelection = async () => {
    const paths = selectedEntries.map((entry) => getEntryPath(currentPath, entry.name));
    if (!paths.length) return;

    setBusyAction("bulk-delete");
    setGlobalLoading(true);

    try {
      await axios.delete(isMediaScope ? "/api/upload/media/r1/batch" : `/api/storage/${scope}/batch`, { data: { paths } });
      setSelectedPaths(new Set());
      showNotification("success", `${paths.length} itens removidos.`);
      await loadTree({ showLoading: false });
    } catch (requestError) {
      showNotification("danger", await getStorageErrorMessage(requestError, "Não foi possível remover os itens selecionados."));
    } finally {
      setGlobalLoading(false);
      setBusyAction("");
    }
  };

  const confirmDelete = (entry) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Remover item"
          description={entry.type === "directory"
            ? `A pasta “${entry.name}” e todo o seu conteúdo serão removidos do servidor remoto.`
            : isMediaScope
              ? `O arquivo de mídia “${entry.name}” será removido do servidor.`
              : `O arquivo “${entry.name}” será removido do servidor remoto.`}
          action={() => handleDelete(entry)}
          onClose={onClose}
          textConfirm="Remover"
          textCancel="Cancelar"
        />
      ),
    });
  };

  const confirmDeleteSelection = () => {
    if (!selectedEntries.length) return;

    confirmAlert({
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Remover itens"
          description={`${selectedEntries.length} ${isMediaScope ? "arquivos de mídia selecionados serão removidos do servidor" : "itens selecionados e todo o conteúdo das pastas serão removidos do servidor remoto"}.`}
          action={() => handleDeleteSelection()}
          onClose={onClose}
          textConfirm="Remover"
          textCancel="Cancelar"
        />
      ),
    });
  };

  const tableData = entries.map((entry) => {
    const relativePath = getEntryPath(currentPath, entry.name);
    const isBusy = busyAction.includes(relativePath);
    const isDirectory = entry.type === "directory";
    const isSymlink = entry.type === "symlink";
    const actionDisabled = Boolean(busyAction) || isBusy;
    const storageAccessUrl = !isMediaScope && !isSymlink ? getFolderAccessUrl(scope, relativePath) : "";
    const mediaAccessUrl = isMediaScope ? getMediaAssetUrl(entry) : "";
    const mediaKind = getMediaKind(entry.name);
    const mediaNameIcon = mediaKind === "image"
      ? faFileImage
      : mediaKind === "video"
        ? faFileVideo
        : mediaKind === "audio"
          ? faFileAudio
          : faFile;

    return {
      name: entry.name,
      preview: isMediaScope ? <MediaPreview entry={entry} /> : null,
      selectCell: (
        <Form.Check
          type="checkbox"
          checked={selectedPaths.has(relativePath)}
          onChange={(event) => handleSelectEntry(entry, event.target.checked)}
          disabled={actionDisabled || isSymlink}
          aria-label={`Selecionar ${entry.name}`}
        />
      ),
      nameCell: isMediaScope && mediaAccessUrl ? (
        <a
          href={mediaAccessUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-body text-decoration-underline d-inline-flex align-items-center gap-2"
          title={mediaAccessUrl}
        >
          <FontAwesomeIcon icon={mediaNameIcon} className="text-secondary" />
          {entry.name}
        </a>
      ) : isSymlink ? (
        <span className="text-muted d-inline-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faFile} />
          {entry.name}
        </span>
      ) : (
        <a
          href="#"
          className="text-body text-decoration-underline d-inline-flex align-items-center gap-2"
          onClick={(event) => {
            event.preventDefault();
            if (isDirectory) {
              handleNavigate(relativePath);
            } else {
              setPropertyEntry(entry);
            }
          }}
        >
          <FontAwesomeIcon icon={isDirectory ? faFolder : faFile} className={isDirectory ? "text-warning" : "text-secondary"} />
          {entry.name}
        </a>
      ),
      type: isDirectory ? "Pasta" : isSymlink ? "Link simbólico" : "Arquivo",
      size: isDirectory ? null : Number(entry.size || 0),
      sizeLabel: isDirectory ? "—" : formatBytes(entry.size),
      modifiedAt: entry.modified_at,
      accessSort: mediaAccessUrl || storageAccessUrl,
      accessCell: mediaAccessUrl ? (
        <a
          href={mediaAccessUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-body text-decoration-underline text-break"
          title={mediaAccessUrl}
        >
          {mediaAccessUrl}
        </a>
      ) : !isMediaScope && storageAccessUrl ? (
        <a
          href={storageAccessUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-body text-decoration-underline text-break"
          title={storageAccessUrl}
        >
          {storageAccessUrl}
        </a>
      ) : "—",
      icons: isMediaScope ? (
        <>
          {!isSymlink && (
            <OverlayTrigger overlay={<Tooltip>Remover</Tooltip>}>
              <Trash
                role="button"
                aria-label="Remover"
                className={`align-middle me-2 cursor-pointer text-body ${actionDisabled ? "opacity-50 pe-none" : ""}`}
                onClick={() => !actionDisabled && confirmDelete(entry)}
                size={18}
              />
            </OverlayTrigger>
          )}
          <OverlayTrigger overlay={<Tooltip>Propriedades</Tooltip>}>
            <Info
              role="button"
              aria-label="Propriedades"
              className={`align-middle me-2 cursor-pointer text-body ${actionDisabled ? "opacity-50 pe-none" : ""}`}
              onClick={() => !actionDisabled && setPropertyEntry(entry)}
              size={18}
            />
          </OverlayTrigger>
          {!isSymlink && (
            <OverlayTrigger overlay={<Tooltip>Renomear</Tooltip>}>
              <Edit
                role="button"
                aria-label="Renomear"
                className={`align-middle me-2 cursor-pointer text-body ${actionDisabled ? "opacity-50 pe-none" : ""}`}
                onClick={() => {
                  if (actionDisabled) return;
                  setRenameEntry(entry);
                  setRenameName(entry.name);
                }}
                size={18}
              />
            </OverlayTrigger>
          )}
          {!isDirectory && !isSymlink && (
            <OverlayTrigger overlay={<Tooltip>Baixar</Tooltip>}>
              <Download
                role="button"
                aria-label="Baixar"
                className={`align-middle cursor-pointer text-body ${actionDisabled ? "opacity-50 pe-none" : ""}`}
                onClick={() => !actionDisabled && handleDownload(entry)}
                size={18}
              />
            </OverlayTrigger>
          )}
        </>
      ) : (
        <>
          {!isSymlink && (
            <OverlayTrigger overlay={<Tooltip>Remover</Tooltip>}>
              <Trash
                role="button"
                aria-label="Remover"
                className={`align-middle me-2 cursor-pointer text-body ${actionDisabled ? "opacity-50 pe-none" : ""}`}
                onClick={() => !actionDisabled && confirmDelete(entry)}
                size={18}
              />
            </OverlayTrigger>
          )}
          <OverlayTrigger overlay={<Tooltip>Propriedades</Tooltip>}>
            <Info
              role="button"
              aria-label="Propriedades"
              className={`align-middle me-2 cursor-pointer text-body ${actionDisabled ? "opacity-50 pe-none" : ""}`}
              onClick={() => !actionDisabled && setPropertyEntry(entry)}
              size={18}
            />
          </OverlayTrigger>
          {!isSymlink && (
            <OverlayTrigger overlay={<Tooltip>Renomear</Tooltip>}>
              <Edit
                role="button"
                aria-label="Renomear"
                className={`align-middle me-2 cursor-pointer text-body ${actionDisabled ? "opacity-50 pe-none" : ""}`}
                onClick={() => {
                  if (actionDisabled) return;
                  setRenameEntry(entry);
                  setRenameName(entry.name);
                }}
                size={18}
              />
            </OverlayTrigger>
          )}
          {isDirectory && !isSymlink && (
            <OverlayTrigger overlay={<Tooltip>Baixar ZIP</Tooltip>}>
              <Download
                role="button"
                aria-label="Baixar ZIP"
                className={`align-middle cursor-pointer text-body ${actionDisabled ? "opacity-50 pe-none" : ""}`}
                onClick={() => !actionDisabled && handleDownloadArchive(entry)}
                size={18}
              />
            </OverlayTrigger>
          )}
          {!isDirectory && !isSymlink && (
            <OverlayTrigger overlay={<Tooltip>Baixar</Tooltip>}>
              <Download
                role="button"
                aria-label="Baixar"
                className={`align-middle cursor-pointer text-body ${actionDisabled ? "opacity-50 pe-none" : ""}`}
                onClick={() => !actionDisabled && handleDownload(entry)}
                size={18}
              />
            </OverlayTrigger>
          )}
        </>
      ),
    };
  });

  const tableColumns = [
    {
      Header: (
        <Form.Check
          type="checkbox"
          checked={allEntriesSelected}
          onChange={handleSelectAll}
          disabled={Boolean(busyAction) || !selectableEntries.length}
          aria-label="Selecionar todos"
        />
      ),
      accessor: "select",
      disableSortBy: true,
      disableFilters: true,
      Filter: false,
      Cell: ({ row }) => row.original.selectCell,
    },
    ...(isMediaScope ? [{
      Header: "Prévia",
      accessor: "preview",
      disableSortBy: true,
      disableFilters: true,
      Filter: false,
      Cell: ({ row }) => row.original.preview,
    }] : []),
    {
      Header: "Nome",
      accessor: "name",
      Cell: ({ row }) => row.original.nameCell,
    },
    {
      Header: "Tipo",
      accessor: "type",
    },
    {
      Header: "Tamanho",
      accessor: "size",
      Cell: ({ row }) => row.original.sizeLabel,
    },
    {
      Header: "Link de acesso",
      accessor: "accessSort",
      disableSortBy: true,
      Cell: ({ row }) => row.original.accessCell,
    },
    {
      Header: "Última modificação",
      accessor: "modifiedAt",
      Cell: ({ value }) => formatModifiedDate(value),
    },
    {
      Header: "",
      accessor: "icons",
      disableSortBy: true,
      Filter: false,
    },
  ];

  if (tipo !== 1) return <Navigate to="/" replace />;

  return (
    <Container fluid className="p-0 container position-relative">
      {selectedEntries.length === 0 && (
        <Button
          type="button"
          variant="primary"
          className="btn update-side-panel-button"
          onClick={loadTree}
          disabled={loading || Boolean(busyAction)}
          title="Atualizar armazenamento"
          aria-label="Atualizar armazenamento"
        >
          <FontAwesomeIcon icon={faSync} spin={loading} size="lg" />
        </Button>
      )}
      {selectedEntries.length > 0 && (
        <div
          className="d-flex align-items-center justify-content-end gap-2 px-4"
          style={{
            position: "fixed",
            top: 0,
            left: selectionBarMetrics.left,
            right: 0,
            height: selectionBarMetrics.height,
            zIndex: 1040,
            backgroundColor: "#fff",
            boxShadow: "0 0 2rem 0 rgba(0, 0, 0, 0.1)",
          }}
        >
          <div className="position-absolute top-50 start-0 translate-middle-y ms-4">
            <OverlayTrigger placement="bottom" overlay={<Tooltip>Limpar seleção</Tooltip>}>
              <Button
                variant="primary"
                onClick={() => setSelectedPaths(new Set())}
                aria-label="Limpar seleção"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </Button>
            </OverlayTrigger>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="small text-muted me-1">
              {selectedEntries.length} {selectedEntries.length === 1 ? "item selecionado" : "itens selecionados"}
            </span>
            <OverlayTrigger placement="bottom" overlay={<Tooltip>Remover selecionados</Tooltip>}>
              <Button
                variant="danger"
                onClick={confirmDeleteSelection}
                disabled={Boolean(busyAction)}
                aria-label="Remover selecionados"
              >
                <FontAwesomeIcon icon={faTrashIcon} />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="bottom" overlay={<Tooltip>Baixar ZIP dos selecionados</Tooltip>}>
              <Button
                variant="primary"
                onClick={handleDownloadSelection}
                disabled={Boolean(busyAction)}
                aria-label="Baixar ZIP dos selecionados"
              >
                <FontAwesomeIcon icon={faFileZipper} />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="bottom" overlay={<Tooltip>Baixar itens individualmente</Tooltip>}>
              <Button
                variant="primary"
                onClick={handleDownloadSelectionIndividually}
                disabled={Boolean(busyAction)}
                aria-label="Baixar itens individualmente"
              >
                <FontAwesomeIcon icon={faDownload} />
              </Button>
            </OverlayTrigger>
          </div>
        </div>
      )}
      <Breadcrumb>
        <Breadcrumb.Item active>Armazenamento</Breadcrumb.Item>
      </Breadcrumb>
      <h6 className="text-muted mb-0">
        {isMediaScope
          ? "Gerencie os arquivos de mídia locais no servidor"
          : `Gerencie arquivos e pastas remotos no ${selectedScope.serverLabel}`}
      </h6>

      <Row className="mt-3">
        <Col md={12}>
          {error && <Alert variant="danger" className="p-2">{error}</Alert>}
          <Card className="mb-3">
            <Card.Body>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div className="d-flex gap-2" aria-label="Escopo do armazenamento">
                  {STORAGE_SCOPES.map((item) => (
                    <Button
                      key={item.key}
                      variant={scope === item.key ? "primary" : "outline-primary"}
                      onClick={() => handleScopeChange(item.key)}
                      disabled={Boolean(busyAction)}
                    >
                      {item.label}
                    </Button>
                  ))}
                </div>
                <div className="w-100 small text-muted mt-1">
                  <div>{selectedScope.description}</div>
                  {selectedScope.domain && (
                    <div>
                      Domínio:{" "}
                      <a
                        href={selectedScope.domain}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-body text-decoration-underline"
                      >
                        {selectedScope.domain}
                      </a>
                    </div>
                  )}
                </div>
                <span className="w-100 small text-muted">
                  {isMediaScope
                    ? `Até ${formatCount(MEDIA_UPLOAD_MAX_FILES)} arquivos por envio · ${formatBytes(MEDIA_UPLOAD_MAX_FILE_BYTES)} por arquivo · imagens, áudios e vídeos`
                    : `Até ${formatCount(limits.maxFiles)} arquivos por envio · ${formatBytes(limits.maxFileBytes)} por arquivo · ${formatBytes(limits.maxTotalBytes)} por lote`}
                </span>
                {uploadProgress && (
                  <div className="w-100 small text-muted" aria-live="polite">
                    <div className="d-flex justify-content-between mb-1">
                      <span>Enviando arquivos...</span>
                      <span>{formatCount(uploadProgress.completedFiles)} de {formatCount(uploadProgress.totalFiles)}</span>
                    </div>
                    <ProgressBar
                      now={uploadProgress.totalBytes > 0
                        ? Math.min(100, ((uploadProgress.completedBytes + uploadProgress.chunkLoadedBytes) / uploadProgress.totalBytes) * 100)
                        : uploadProgress.totalFiles > 0
                          ? (uploadProgress.completedFiles / uploadProgress.totalFiles) * 100
                          : 100}
                      label={`${Math.round(uploadProgress.totalBytes > 0
                        ? ((uploadProgress.completedBytes + uploadProgress.chunkLoadedBytes) / uploadProgress.totalBytes) * 100
                        : (uploadProgress.completedFiles / uploadProgress.totalFiles) * 100)}%`}
                    />
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div className="d-flex align-items-center gap-2">
                  {!isMediaScope && currentPath && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleNavigate(getParentPath(currentPath))}
                      disabled={Boolean(busyAction)}
                      title="Pasta anterior"
                      aria-label="Pasta anterior"
                    >
                      <FontAwesomeIcon icon={faArrowLeft} />
                    </Button>
                  )}
                  <span className="text-muted">{isMediaScope ? "Mídia" : currentPath || "Raiz"}</span>
                </div>
                <div className="d-flex gap-2">
                  {!isMediaScope && (
                    <Button
                      variant="primary"
                      onClick={() => setFolderModalOpen(true)}
                      disabled={Boolean(busyAction)}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-2" />
                      Nova pasta
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={Boolean(busyAction) || (!isMediaScope && !currentPath)}
                    title={busyAction === "upload"
                      ? "Enviando arquivos..."
                      : isMediaScope || currentPath ? "Enviar arquivos" : "Entre em uma pasta para enviar arquivos"}
                    aria-label="Enviar arquivos"
                  >
                    <FontAwesomeIcon icon={faUpload} />
                  </Button>
                  {!isMediaScope && (
                    <Button
                      variant="outline-primary"
                      onClick={() => folderInputRef.current?.click()}
                      disabled={Boolean(busyAction) || !currentPath}
                      title={busyAction === "upload"
                        ? "Enviando pasta..."
                        : currentPath ? "Enviar pasta" : "Entre em uma pasta para enviar uma pasta"}
                      aria-label="Enviar pasta"
                    >
                      <FontAwesomeIcon icon={faFolder} />
                    </Button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={isMediaScope ? MEDIA_UPLOAD_ACCEPT : undefined}
                    className="d-none"
                    onChange={handleUpload}
                  />
                  {!isMediaScope && (
                    <input
                      ref={folderInputRef}
                      type="file"
                      multiple
                      webkitdirectory=""
                      directory=""
                      className="d-none"
                      onChange={handleUpload}
                    />
                  )}
                </div>
              </div>

              {loading && !hasLoadedOnce ? (
                <div className="d-flex justify-content-center align-items-center gap-2 text-muted py-5">
                  <Spinner animation="border" size="sm" />
                  Carregando arquivos...
                </div>
              ) : (
                <ColumnSortingTable
                  key={`${scope}:${currentPath}:${tableInstanceVersion}`}
                  removeCard
                  columns={tableColumns}
                  data={tableData}
                  title="Armazenamento"
                  getCellProps={(cellinfo) => ({
                    className: cellinfo.column.id === "icons"
                      ? "table-icons-triple"
                      : cellinfo.column.id === "select" ? "text-center" : cellinfo.column.id === "preview" ? "media-preview-cell" : "",
                    style: cellinfo.column.id === "icons"
                      ? { whiteSpace: "nowrap" }
                      : cellinfo.column.id === "select" ? { width: "48px" } : cellinfo.column.id === "preview" ? { width: "86px", minWidth: "86px" } : undefined,
                  })}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={folderModalOpen} onHide={() => setFolderModalOpen(false)} centered>
        <Form onSubmit={handleCreateFolder}>
          <Modal.Header closeButton>
            <Modal.Title>Nova pasta</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Nome da pasta</Form.Label>
              <Form.Control
                autoFocus
                value={folderName}
                onChange={(event) => setFolderName(event.target.value)}
                placeholder="ex.: imagens"
                disabled={busyAction === "folder"}
              />
              <Form.Text className="text-muted">A pasta será criada em {currentPath || "raiz"}.</Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setFolderModalOpen(false)} disabled={busyAction === "folder"}>Cancelar</Button>
            <Button type="submit" variant="primary" disabled={busyAction === "folder"}>
              {busyAction === "folder" ? "Criando..." : "Criar pasta"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(renameEntry)} onHide={() => setRenameEntry(null)} centered>
        <Form onSubmit={handleRename}>
          <Modal.Header closeButton>
            <Modal.Title>Renomear item</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Novo nome</Form.Label>
              <Form.Control
                autoFocus
                value={renameName}
                onChange={(event) => setRenameName(event.target.value)}
                disabled={busyAction.startsWith("rename:")}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setRenameEntry(null)} disabled={busyAction.startsWith("rename:")}>Cancelar</Button>
            <Button type="submit" variant="primary" disabled={busyAction.startsWith("rename:")}>Renomear</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(propertyEntry)} onHide={() => setPropertyEntry(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Propriedades</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {propertyEntry && (
            <dl className="row mb-0">
              <dt className="col-sm-4">Nome</dt>
              <dd className="col-sm-8 text-break">{propertyEntry.name}</dd>
              <dt className="col-sm-4">Caminho</dt>
              <dd className="col-sm-8 text-break">{getEntryPath(currentPath, propertyEntry.name)}</dd>
              <dt className="col-sm-4">Tipo</dt>
              <dd className="col-sm-8">{propertyEntry.type === "directory" ? "Pasta" : "Arquivo"}</dd>
              <dt className="col-sm-4">Tamanho</dt>
              <dd className="col-sm-8">{propertyEntry.type === "directory" ? "—" : formatBytes(propertyEntry.size)}</dd>
              <dt className="col-sm-4">Modificado em</dt>
              <dd className="col-sm-8">{formatModifiedDate(propertyEntry.modified_at)}</dd>
            </dl>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default StorageManager;
